import { apiClient } from "@/lib/api-client";
import type {
  EmailDocument,
  EmailBlock,
  EmailDocumentVersion,
  PagedResult,
} from "@/types/email";

/**
 * Email Builder API Service
 * Handles all API calls for email builder functionality
 */
export class EmailBuilderService {
  private basePath = "/email-builder";

  // ===== DOCUMENTS =====

  /**
   * Get paginated list of email documents
   */
  async getDocuments(params?: {
    page?: number;
    pageSize?: number;
    status?: "Draft" | "Published" | "Archived";
  }): Promise<PagedResult<EmailDocument>> {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.pageSize !== undefined) queryParams.pageSize = params.pageSize;
    if (params?.status !== undefined) queryParams.status = params.status;

    return apiClient.get(`${this.basePath}/documents`, {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      requiresAuth: true,
    });
  }

  /**
   * Get single document with all blocks
   */
  async getDocument(id: string): Promise<EmailDocument> {
    return apiClient.get(`${this.basePath}/documents/${id}`, {
      requiresAuth: true,
    });
  }

  /**
   * Create new blank document
   */
  async createDocument(data: {
    name: string;
  }): Promise<EmailDocument> {
    return apiClient.post(`${this.basePath}/documents`, data, {
      requiresAuth: true,
    });
  }

  /**
   * Create document from template
   */
  async createFromTemplate(
    templateId: string,
    name: string,
  ): Promise<EmailDocument> {
    return apiClient.post(
      `${this.basePath}/documents/from-template/${templateId}`,
      { name },
      { requiresAuth: true }
    );
  }

  /**
   * Update document metadata (uses PUT for full updates)
   */
  async updateDocument(
    id: string,
    data: {
      name?: string;
      configuration?: string;
      status?: "Draft" | "Published" | "Archived";
    },
  ): Promise<EmailDocument> {
    return apiClient.put(`${this.basePath}/documents/${id}`, data, {
      requiresAuth: true,
    });
  }

  /**
   * Rename document (uses PATCH for partial update)
   */
  async renameDocument(id: string, name: string): Promise<EmailDocument> {
    return apiClient.patch(`${this.basePath}/documents/${id}/rename`, 
      { name },
      { requiresAuth: true }
    );
  }

  /**
   * Delete document
   */
  async deleteDocument(id: string): Promise<void> {
    return apiClient.delete(`${this.basePath}/documents/${id}`, {
      requiresAuth: true,
    });
  }

  // ===== BLOCKS =====

  /**
   * Add new block to document
   */
  async addBlock(
    documentId: string,
    data: {
      blockType: string;
      parentBlockId?: string;
      orderIndex: number;
      configuration?: Record<string, unknown>;
    },
  ): Promise<EmailBlock> {
    // Backend expects configuration as stringified JSON with type discriminator
    const configWithType = data.configuration 
      ? { $blockType: data.blockType, ...data.configuration }
      : { $blockType: data.blockType };
    
    const requestBody = {
      ...data,
      configuration: JSON.stringify(configWithType),
    };
    
    return apiClient.post(
      `${this.basePath}/documents/${documentId}/blocks`,
      requestBody,
      { requiresAuth: true }
    );
  }

  /**
   * Update block configuration
   */
  async updateBlock(documentId: string, blockId: string, blockType: string, configuration: Record<string, unknown>): Promise<EmailBlock> {
    // Backend expects configuration as stringified JSON with type discriminator
    const configWithType = { $blockType: blockType, ...configuration };
    
    return apiClient.put(
      `${this.basePath}/documents/${documentId}/blocks/configuration`,
      { id: blockId, configuration: JSON.stringify(configWithType) },
      { requiresAuth: true }
    );
  }

  /**
   * Move block to new parent/position
   */
  async moveBlock(
    documentId: string,
    blockId: string,
    newParentId: string | undefined,
    newOrderIndex: number,
  ): Promise<EmailBlock> {
    return apiClient.post(
      `${this.basePath}/documents/${documentId}/blocks/move`,
      { id: blockId, newParentId, newOrderIndex },
      { requiresAuth: true }
    );
  }

  /**
   * Delete block (cascades to children)
   */
  async deleteBlock(documentId: string, blockId: string): Promise<void> {
    return apiClient.delete(`${this.basePath}/documents/${documentId}/blocks/${blockId}`, {
      requiresAuth: true,
    });
  }

  /**
   * Duplicate block and all children
   */
  async duplicateBlock(
    documentId: string,
    sourceBlockId: string,
    targetParentId?: string,
  ): Promise<EmailBlock> {
    return apiClient.post(
      `${this.basePath}/documents/${documentId}/blocks/duplicate`,
      { sourceBlockId, targetParentId },
      { requiresAuth: true }
    );
  }

  // ===== VERSIONS =====

  /**
   * Get version history for document
   */
  async getVersions(documentId: string): Promise<EmailDocumentVersion[]> {
    return apiClient.get(`${this.basePath}/documents/${documentId}/versions`, {
      requiresAuth: true,
    });
  }

  /**
   * Create manual version/checkpoint
   */
  async createVersion(
    documentId: string,
    changeDescription?: string,
    changeType: "Manual" | "AutoSave" = "Manual",
  ): Promise<EmailDocumentVersion> {
    return apiClient.post(
      `${this.basePath}/documents/${documentId}/versions`,
      { changeDescription, changeType },
      { requiresAuth: true }
    );
  }

  /**
   * Restore document to specific version
   */
  async restoreVersion(
    documentId: string,
    versionNumber: number,
  ): Promise<{ message: string }> {
    return apiClient.post(
      `${this.basePath}/documents/${documentId}/versions/${versionNumber}/restore`,
      {},
      { requiresAuth: true }
    );
  }

  async previewDocument(documentId: string): Promise<{ html: string }> {
    return apiClient.post(
      `${this.basePath}/documents/preview`,
      {"emailDocumentId": documentId},
      { requiresAuth: true }
    );
  }
}

// Export singleton instance
export const emailBuilderService = new EmailBuilderService();
